import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import {
  Camera,
  Gem,
  FolderHeart,
  Pickaxe,
  GraduationCap,
  BookOpen,
  Users,
  Menu,
  Mountain,
  HardHat,
  Trophy,
  Sparkles,
  HelpCircle,
  Download,
  MoreHorizontal,
  BookOpenCheck,
  Scale,
} from "lucide-react";
import { ShellButton } from "@/components/layout/ShellControl";
import { shellControlClassName } from "@/components/layout/shellControlClasses";
import { ThemeToggle } from "@/components/theme/ThemeToggle";
import { CommandSearch } from "@/components/layout/CommandSearch";
import { AuthMenu } from "@/components/layout/AuthMenu";
import { useGuideShareLanding, type SharedGuideType } from "@/hooks/useGuideShareLanding";
import { useIsAdmin } from "@/hooks/useIsAdmin";

const MoreNavMenu = lazy(() => import("@/components/layout/MoreNavMenu"));
const MobileNavDrawer = lazy(() => import("@/components/layout/MobileNavDrawer"));
const PWAStatusHosts = lazy(() => import("@/components/pwa/PWAStatusHosts"));
const SiteFooter = lazy(() => import("@/components/home/SiteFooter").then((module) => ({ default: module.SiteFooter })));
const RelatedResourceShelfContent = lazy(() => import("@/components/guides/RelatedResourceShelf"));

const navItems = [
  { path: "/guides", label: "Guides & Tools", icon: BookOpenCheck },
  { path: "/tools", label: "Calculators", icon: Scale },
  { path: "/identify", label: "Identify", icon: Camera },
  { path: "/quick-id", label: "Quick ID", icon: Gem },
  { path: "/my-finds", label: "My Finds", icon: FolderHeart },
  { path: "/pro-tools", label: "Pro Tools", icon: Pickaxe },
  { path: "/training", label: "Training", icon: GraduationCap },
  { path: "/careers", label: "Careers", icon: HardHat },
  { path: "/reference", label: "Reference", icon: BookOpen },
  { path: "/community", label: "Community", icon: Users },
  { path: "/leaderboard", label: "Ranks", icon: Trophy },
];

const primaryNavPaths = new Set(["/identify", "/quick-id", "/tools", "/training", "/reference"]);
const primaryNavItems = navItems.filter((item) => primaryNavPaths.has(item.path));
const moreNavItems = [
  { path: "/features", label: "How it works", icon: HelpCircle },
  { path: "/install", label: "Install App", icon: Download },
  { path: "/finds", label: "Public Finds", icon: Sparkles },
  ...navItems.filter((item) => !primaryNavPaths.has(item.path)),
];

const relatedResourcePath = /^(?:\/guides\/[^/]+|\/compare\/[^/]+|\/identify\/[^/]+|\/tools\/[^/]+|\/quiz\/[^/]+)$/;

function layoutShareLandingType(pathname: string): SharedGuideType | null {
  if (/^\/tools\/[^/]+$/.test(pathname) || pathname === "/guides/geologic-time-scale") return "property";
  return null;
}

function DeferredRelatedResourceShelf({ pathname }: { pathname: string }) {
  const [ready, setReady] = useState(false);
  const markerRef = useRef<HTMLDivElement>(null);
  const eligible = relatedResourcePath.test(pathname);

  useEffect(() => {
    if (!eligible || ready) return;
    const marker = markerRef.current;
    const timer = window.setTimeout(() => setReady(true), 3_000);
    if (!marker || typeof window.IntersectionObserver !== "function") {
      return () => window.clearTimeout(timer);
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      window.clearTimeout(timer);
      setReady(true);
    }, { rootMargin: "1200px 0px" });
    observer.observe(marker);
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [eligible, ready]);

  if (!eligible) return null;
  return (
    <div ref={markerRef} data-related-resource-boundary>
      {ready && (
        <Suspense fallback={null}>
          <RelatedResourceShelfContent pathname={pathname} />
        </Suspense>
      )}
    </div>
  );
}


export function MainLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const { isAdmin } = useIsAdmin();
  useGuideShareLanding(layoutShareLandingType(location.pathname));

  const handleMobileMenuOpenChange = (open: boolean) => {
    setMobileMenuOpen(open);
    if (!open) {
      queueMicrotask(() => mobileMenuTriggerRef.current?.focus());
    }
  };

  return (
    <div className="min-h-screen min-w-0 overflow-x-clip bg-background">
      {/* Desktop Header */}
      <header className="hidden md:block fixed top-0 inset-x-0 z-50 glass border-b">
        <div className="container">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex shrink-0 items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
                <Mountain className="h-6 w-6 text-primary-foreground" />
              </div>
              <div className="flex flex-col">
                <span className="font-display text-lg font-bold text-foreground">
                  GeoMiner
                </span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  By a Geologist
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="flex min-w-0 items-center gap-1">
              {primaryNavItems.map((item) => {
                const isActive = location.pathname.startsWith(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    aria-current={isActive ? "page" : undefined}
                    className={shellControlClassName({
                      variant: isActive ? "default" : "ghost",
                      size: "nav",
                    })}
                  >
                    <item.icon className="h-4 w-4" />
                    <span className="hidden xl:inline">{item.label}</span>
                  </Link>
                );
              })}
              <Suspense
                fallback={
                  <ShellButton
                    type="button"
                    variant={moreNavItems.some((item) => location.pathname.startsWith(item.path)) ? "default" : "ghost"}
                    size="nav"
                    aria-label="Loading more navigation"
                    disabled
                  >
                    <MoreHorizontal className="h-4 w-4" />
                    <span className="hidden xl:inline">More</span>
                    <span className="sr-only xl:hidden">More navigation</span>
                  </ShellButton>
                }
              >
                <MoreNavMenu items={moreNavItems} pathname={location.pathname} />
              </Suspense>
              <CommandSearch />
              <ThemeToggle />
              <div className="ml-2 pl-2 border-l border-border/60">
                <AuthMenu variant="desktop" />
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Desktop header spacer */}
      <div className="hidden md:block h-16" />

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 inset-x-0 z-50 glass border-b">
        <div className="flex h-14 min-w-0 items-center justify-between gap-2 px-4">
          <Link to="/" className="flex min-w-0 shrink items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
              <Mountain className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="truncate font-display text-lg font-bold">GeoMiner</span>
          </Link>
          <div className="flex shrink-0 items-center gap-0.5">
            <CommandSearch mobile />
            <ThemeToggle />
            <AuthMenu variant="mobile" />
            <ShellButton
              ref={mobileMenuTriggerRef}
              variant="ghost"
              size="icon"
              onClick={() => handleMobileMenuOpenChange(true)}
              aria-label="Open menu"
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation-drawer"
            >
              <Menu className="h-6 w-6" />
            </ShellButton>
          </div>
        </div>
      </header>

      {/* Mount the lazy boundary immediately so the drawer chunk is ready before the first tap. */}
      <Suspense fallback={null}>
        <MobileNavDrawer
          open={mobileMenuOpen}
          onOpenChange={handleMobileMenuOpenChange}
          pathname={location.pathname}
          items={navItems}
          isAdmin={isAdmin}
        />
      </Suspense>

      {/* Mobile header spacer */}
      <div className="h-14 md:hidden" />

      {/* Keep the install prompt below the fixed header so navigation remains usable. */}
      <Suspense fallback={null}>
        <PWAStatusHosts />
      </Suspense>

      {/* Main Content */}
      <main className="min-h-[calc(100vh-3.5rem)] min-w-0 pb-24 md:min-h-[calc(100vh-4rem)] md:pb-8">
          <div key={location.pathname}>
            <Outlet />
          </div>
      </main>

      <DeferredRelatedResourceShelf pathname={location.pathname} />

      <Suspense fallback={null}>
        <SiteFooter />
      </Suspense>

      {/* Bottom nav is now rendered globally via MobileBottomNav in App.tsx */}
    </div>
  );
}
